import { parseStyleAttr } from "../blocks/html-parse";
import { readStyle, writeStyle } from "../blocks/shared-block-styles";
import {
  EMPTY_STYLE_MAP,
  toCssPropertyName,
} from "../blocks/shared-block-styles";
import { Extension } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Color from "@tiptap/extension-color";
import Document from "@tiptap/extension-document";
import Dropcursor from "@tiptap/extension-dropcursor";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Strike from "@tiptap/extension-strike";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import { FontFamily, FontSize, TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";

import { LinkBubbleMenuHandler } from "./link-bubble-menu-handler";

/** Inline properties the marks alongside this one already model. */
const MODELLED_INLINE_PROPERTIES = new Set([
  "color",
  "font-size",
  "font-family",
]);

/**
 * A span's styles, as written.
 *
 * `TextStyle` and the marks built on it name three properties — colour, size,
 * family — and everything else a designer put on a `<span>` was dropped. That
 * is the same cap the blocks used to have, one level down, and it is why a
 * navigation strip written `letter-spacing:2px;text-transform:uppercase` came
 * back as ordinary sentence-case text.
 *
 * The three modelled properties are left to the marks that own them, so nothing
 * is written twice.
 */
const EmailTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: EMPTY_STYLE_MAP,
        parseHTML: (element: HTMLElement) => parseStyleAttr(element),
        renderHTML: (attributes: Record<string, unknown>) => {
          const stored = (attributes.style ?? {}) as Record<string, string>;
          const rest = Object.entries(stored)
            .filter(([property]) => {
              const css = property.startsWith("--")
                ? property
                : toCssPropertyName(property);
              return !MODELLED_INLINE_PROPERTIES.has(css);
            })
            .map(([property, value]) => `${property}: ${value}`);
          return rest.length > 0 ? { style: rest.join("; ") } : {};
        },
      },
    };
  },
});

/**
 * Custom Document extension that restricts content to email blocks.
 * Overrides the default Document to only accept emailBlock group nodes
 * and standard list nodes at the top level.
 */
const EmailDocument = Document.extend({
  content: "(emailRow | bulletList | orderedList)+",
});

/**
 * Configured Link extension for email use.
 * - openOnClick: false — prevents navigating away while editing
 * - autolink: true — auto-detects URLs as you type
 */
const EmailLink = Link.configure({
  openOnClick: false,
  autolink: true,
  HTMLAttributes: {
    target: "_blank",
    rel: "noopener noreferrer",
  },
});

/**
 * TextAlign extension configured for email block types.
 * Only applies to our custom email blocks that support alignment.
 */
const EmailTextAlign = TextAlign.configure({
  types: ["emailHeading", "emailParagraph", "emailBlockquote"],
});

/**
 * Mark extensions for inline text formatting.
 * These are standard Tiptap v3 extensions with default configuration.
 */
export const emailMarkExtensions = [
  Bold,
  Italic,
  Underline,
  Strike,
  EmailLink,
  LinkBubbleMenuHandler,
  EmailTextStyle,
  FontFamily,
  FontSize,
  Color,
  EmailTextAlign,
];

/**
 * Structural extensions required by the editor.
 * These handle document structure, lists, cursor behavior, and undo/redo.
 */
export const emailStructuralExtensions = [
  EmailDocument,
  Text,
  HardBreak,
  History,
  Dropcursor.configure({
    color: "rgb(var(--link))",
    width: 2,
  }),
  BulletList,
  OrderedList,
  ListItem.extend({
    content: "(emailBlock | bulletList | orderedList)+",
    addKeyboardShortcuts() {
      return {
        Enter: () => this.editor.commands.splitListItem(this.name),
      };
    },
  }),
];

const INDENT_PX = 40;

/**
 * Global Tab / Shift-Tab handler.
 * - Inside a list: sink/lift list item
 * - Outside a list: adjust paddingLeft on the current block
 * Always returns true to prevent browser focus-switching.
 */
const EmailIndent = Extension.create({
  name: "emailIndent",
  addKeyboardShortcuts() {
    const adjustIndent = (delta: number) => {
      const editor = this.editor;

      // List indent/outdent
      if (editor.isActive("bulletList") || editor.isActive("orderedList")) {
        if (delta > 0) {
          editor.commands.sinkListItem("listItem");
        } else {
          editor.commands.liftListItem("listItem");
        }
        return true;
      }

      // Block paddingLeft indent/outdent. Read and write through the block's
      // style store, or a block that arrived from a template with its own
      // `padding-left` would take the keystroke and show nothing for it.
      const { $from } = editor.state.selection;
      for (let d = $from.depth; d >= 0; d--) {
        const node = $from.node(d);
        if (
          node.type.spec.group === "emailBlock" &&
          ("paddingLeft" in node.attrs || "style" in node.attrs)
        ) {
          const current =
            parseInt(readStyle(node.attrs, "paddingLeft"), 10) || 0;
          const next = Math.max(0, current + delta);
          const pos = $from.before(d);
          const { tr } = editor.state;
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            ...writeStyle(
              node.attrs,
              "paddingLeft",
              next > 0 ? `${next}px` : "",
            ),
          });
          editor.view.dispatch(tr);
          return true;
        }
      }

      // Always capture Tab to prevent focus switch
      return true;
    };

    return {
      Tab: () => adjustIndent(INDENT_PX),
      "Shift-Tab": () => adjustIndent(-INDENT_PX),
    };
  },
});

/**
 * All non-block extensions needed by the email editor.
 * Block extensions are created separately from the block registry
 * via createNodeExtension().
 *
 * Usage:
 *   const extensions = [
 *     ...emailBaseExtensions,
 *     ...blockRegistry.map(def => createNodeExtension(def)),
 *   ];
 */
export const emailBaseExtensions = [
  ...emailStructuralExtensions,
  ...emailMarkExtensions,
  EmailIndent,
];
